import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Button } from '@repo/design-system/components/ds/button';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { Collapsible } from '@repo/design-system/components/ui/collapsible';
import { Type } from '@repo/design-system/components/ui/type';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useGetFoundPinsQuery } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectActiveHeroActionId, selectHeroActionTriggerPinId, setActiveCustomChecklistId, setHighlightedPinIds, setSidebarView } from '@repo/store/maps/slices/clientContext';
import { selectExternalMapId, selectHeroActionById, selectMapData, selectSelectedMarkerId, selectSteamId } from '@repo/store/maps/slices/mapData';
import type { WebLoginPromptTrigger } from '@repo/types/analytic-events/website-analytic-events';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import { getPinImageUrl, getSteamImageUrl } from '@repo/utility/image-urls';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChecklistFeedback } from '../checklist/checklist-feedback';
import { ChecklistItemRow } from '../checklist/checklist-item-row';
import { SearchFilters } from '../checklist/checklist-search-filters';
import { buildCategoryMap, buildCheckedItems, buildSubcategoryMap, type ChecklistItem, type CompletionFilter, filterChecklistItems, groupItemsByCategory } from '../checklist/checklist-utils';
import { SaveChecklistModal } from '../checklist/save-checklist-modal';
import { useChecklistFeedback } from '../checklist/use-checklist-feedback';
import { useChecklistToggle } from '../checklist/use-checklist-toggle';
import { ExploreActionHeaderIcon } from '../explore/explore-action-header-icon';
import { getFeatureImageUrl, HERO_IMAGE_SIZE_OVERRIDE } from '../explore/explore-sidebar-view';
import { useMapPinOperations } from '../game-map/hooks/useMapPinOperations';
import { MapDiscoveryProgress } from '../map-discovery-progress';
import { SidebarHeroFrame } from '../utils/sidebar-hero-frame';
import { HeroActionDescription } from './hero-action-description';
import { getHeroActionIcon } from './hero-action-icons';
import { getHeroActionResultPinIds } from './hooks/useHeroActionForPin';
interface HeroActionSidebarViewProps {
  onBack: () => void;
  isMobilePane?: boolean;
  onSearchFocus?: () => void;
}
export const HeroActionSidebarView: React.FC<HeroActionSidebarViewProps> = t0 => {
  const $ = _c(169);
  const {
    onBack,
    isMobilePane: t1,
    onSearchFocus
  } = t0;
  const isMobilePane = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["game_map"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useT(t2);
  const mapData = useAppSelector(selectMapData);
  const externalMapId = useAppSelector(selectExternalMapId);
  const steamId = useAppSelector(selectSteamId);
  const activeHeroActionId = useAppSelector(selectActiveHeroActionId);
  const triggerPinId = useAppSelector(selectHeroActionTriggerPinId);
  let t3;
  if ($[1] !== activeHeroActionId) {
    t3 = state => selectHeroActionById(state, activeHeroActionId);
    $[1] = activeHeroActionId;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const heroAction = useAppSelector(t3);
  const selectedMarkerId = useAppSelector(selectSelectedMarkerId);
  const isLoggedIn = useAppSelector(_temp);
  const {
    clearSelectedMarkerInMap,
    selectMarkerInMap
  } = useMapPinOperations();
  const analytics = useAnalytics();
  const dispatch = useAppDispatch();
  const hostType = useHostType();
  const searchInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [completionFilter, setCompletionFilter] = useState("all");
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [loginModalTrigger, setLoginModalTrigger] = useState("checklist-item");
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const t4 = activeHeroActionId ?? undefined;
  let t5;
  if ($[3] !== t4) {
    t5 = {
      anchorLocationId: t4
    };
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleFeedbackRate = useChecklistFeedback("hero-action-list", t5);
  let t6;
  let t7;
  if ($[5] !== heroAction || $[6] !== mapData || $[7] !== onBack) {
    t6 = () => {
      if (mapData && !heroAction) {
        onBack();
      }
    };
    t7 = [mapData, heroAction, onBack];
    $[5] = heroAction;
    $[6] = mapData;
    $[7] = onBack;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  bb0: {
    if (!mapData) {
      t8 = null;
      break bb0;
    }
    let t9;
    if ($[10] !== mapData) {
      t9 = buildSubcategoryMap(mapData);
      $[10] = mapData;
      $[11] = t9;
    } else {
      t9 = $[11];
    }
    let t10;
    if ($[12] !== mapData) {
      t10 = buildCategoryMap(mapData);
      $[12] = mapData;
      $[13] = t10;
    } else {
      t10 = $[13];
    }
    let t11;
    if ($[14] !== t10 || $[15] !== t9) {
      t11 = {
        subcategoryMap: t9,
        categoryMap: t10
      };
      $[14] = t10;
      $[15] = t9;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    t8 = t11;
  }
  const lookups = t8;
  let t9;
  bb1: {
    if (!triggerPinId || !mapData) {
      t9 = null;
      break bb1;
    }
    let t10;
    if ($[17] !== mapData.geojson.features || $[18] !== triggerPinId) {
      t10 = mapData.geojson.features.find(f => f.geometry.type === "Point" && f.properties.externalId === triggerPinId) ?? null;
      $[17] = mapData.geojson.features;
      $[18] = triggerPinId;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    t9 = t10;
  }
  const triggerPin = t9;
  const t10 = !isLoggedIn || !externalMapId;
  let t11;
  if ($[20] !== t10) {
    t11 = {
      skip: t10
    };
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const {
    data: foundPinsData
  } = useGetFoundPinsQuery(externalMapId ?? "", t11);
  let t12;
  if ($[22] !== foundPinsData || $[23] !== isLoggedIn) {
    t12 = buildCheckedItems(isLoggedIn, foundPinsData);
    $[22] = foundPinsData;
    $[23] = isLoggedIn;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const checkedItems = t12;
  const handleToggleItem = useChecklistToggle(externalMapId, checkedItems);
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = open => {
      setIsLoginModalOpen(open);
    };
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const handleLoginModalOpenChange = t13;
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setLoginModalTrigger("checklist-item");
      setIsLoginModalOpen(true);
    };
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const openChecklistItemLoginModal = t14;
  let t15;
  if ($[27] !== handleToggleItem || $[28] !== isLoggedIn) {
    t15 = itemId => {
      if (!isLoggedIn) {
        openChecklistItemLoginModal();
        return;
      }
      handleToggleItem(itemId);
    };
    $[27] = handleToggleItem;
    $[28] = isLoggedIn;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const handleToggleWithLogin = t15;
  let t16;
  bb2: {
    if (!mapData || !heroAction) {
      let t17;
      if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = [];
        $[30] = t17;
      } else {
        t17 = $[30];
      }
      t16 = t17;
      break bb2;
    }
    let t17;
    if ($[31] !== externalMapId || $[32] !== heroAction) {
      t17 = new Set(getHeroActionResultPinIds(heroAction, externalMapId ?? undefined));
      $[31] = externalMapId;
      $[32] = heroAction;
      $[33] = t17;
    } else {
      t17 = $[33];
    }
    const ids = t17;
    let t18;
    if ($[34] !== ids || $[35] !== mapData.geojson.features) {
      let t19;
      if ($[37] !== ids) {
        t19 = f_0 => f_0.geometry.type === "Point" && ids.has(f_0.properties.externalId);
        $[37] = ids;
        $[38] = t19;
      } else {
        t19 = $[38];
      }
      t18 = mapData.geojson.features.filter(t19);
      $[34] = ids;
      $[35] = mapData.geojson.features;
      $[36] = t18;
    } else {
      t18 = $[36];
    }
    t16 = t18;
  }
  const resultFeatures = t16;
  let t17;
  if ($[39] !== resultFeatures) {
    t17 = resultFeatures.map(_temp2);
    $[39] = resultFeatures;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const savePinIds = t17;
  const canShowSave = !!externalMapId && savePinIds.length > 0;
  let t18;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      setLoginModalTrigger("save-hero-list");
      setIsLoginModalOpen(true);
    };
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const openSaveLoginModal = t18;
  let t19;
  if ($[42] !== isLoggedIn) {
    t19 = () => {
      if (!isLoggedIn) {
        return openSaveLoginModal();
      }
      setIsSaveModalOpen(true);
    };
    $[42] = isLoggedIn;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  const handleSaveClick = t19;
  let t20;
  let t21;
  if ($[44] !== dispatch || $[45] !== resultFeatures) {
    t20 = () => {
      const ids_0 = resultFeatures.map(_temp3);
      dispatch(setHighlightedPinIds(ids_0));
      return () => {
        dispatch(setHighlightedPinIds([]));
      };
    };
    t21 = [resultFeatures, dispatch];
    $[44] = dispatch;
    $[45] = resultFeatures;
    $[46] = t20;
    $[47] = t21;
  } else {
    t20 = $[46];
    t21 = $[47];
  }
  useEffect(t20, t21);
  let t22;
  bb3: {
    if (!lookups) {
      let t23;
      if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
        t23 = [];
        $[48] = t23;
      } else {
        t23 = $[48];
      }
      t22 = t23;
      break bb3;
    }
    let out;
    if ($[49] !== lookups || $[50] !== resultFeatures) {
      out = [];
      for (const feature of resultFeatures) {
        const subcategory = lookups.subcategoryMap.get(feature.properties.subcategoryExternalId);
        const category = subcategory ? lookups.categoryMap.get(subcategory.categoryExternalId) : undefined;
        if (!subcategory || !category) {
          continue;
        }
        const coords = feature.geometry.coordinates;
        out.push({
          id: feature.properties.externalId,
          title: feature.properties.title || subcategory.title,
          description: feature.properties.description || "",
          categoryTitle: category.title,
          subcategoryTitle: subcategory.title,
          subcategoryExternalId: subcategory.externalId,
          categoryColor: category.color,
          imagePath: feature.properties.imagePath ?? null,
          imageId: feature.properties.imageId ?? null,
          imageUrl: getPinImageUrl(feature.properties.imageId, feature.properties.imagePath) ?? null,
          coordinates: [coords[0] as number, coords[1] as number]
        });
      }
      $[49] = lookups;
      $[50] = resultFeatures;
      $[51] = out;
    } else {
      out = $[51];
    }
    t22 = out;
  }
  const items = t22;
  const totalCount = items.length;
  let t23;
  if ($[52] !== checkedItems || $[53] !== items) {
    let t24;
    if ($[55] !== checkedItems) {
      t24 = (count, item) => count + (checkedItems.has(item.id) ? 1 : 0);
      $[55] = checkedItems;
      $[56] = t24;
    } else {
      t24 = $[56];
    }
    t23 = items.reduce(t24, 0);
    $[52] = checkedItems;
    $[53] = items;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  const foundCount = t23;
  let t24;
  if ($[57] !== checkedItems || $[58] !== completionFilter || $[59] !== items || $[60] !== searchQuery) {
    t24 = groupItemsByCategory(filterChecklistItems(items, searchQuery, completionFilter, checkedItems));
    $[57] = checkedItems;
    $[58] = completionFilter;
    $[59] = items;
    $[60] = searchQuery;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  const groupedItems = t24;
  let t25;
  if ($[62] !== groupedItems) {
    t25 = Object.keys(groupedItems);
    $[62] = groupedItems;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  const hasAnyItems = t25.length > 0;
  let t26;
  bb4: {
    const t27 = triggerPin ?? undefined;
    let t28;
    if ($[64] !== t27) {
      t28 = getFeatureImageUrl(t27);
      $[64] = t27;
      $[65] = t28;
    } else {
      t28 = $[65];
    }
    const triggerImage = t28;
    if (triggerImage) {
      t26 = triggerImage;
      break bb4;
    }
    let t29;
    if ($[66] !== resultFeatures) {
      const fallback = resultFeatures.find(_temp4);
      t29 = getFeatureImageUrl(fallback);
      $[66] = resultFeatures;
      $[67] = t29;
    } else {
      t29 = $[67];
    }
    const fallbackImage = t29;
    if (fallbackImage) {
      t26 = fallbackImage;
      break bb4;
    }
    let t30;
    if ($[68] !== steamId) {
      t30 = steamId ? getSteamImageUrl(steamId, "hero") : "";
      $[68] = steamId;
      $[69] = t30;
    } else {
      t30 = $[69];
    }
    t26 = t30;
  }
  const backgroundImageUrl = t26;
  let t27;
  if ($[70] !== lookups?.subcategoryMap || $[71] !== triggerPin) {
    t27 = triggerPin ? lookups?.subcategoryMap.get(triggerPin.properties.subcategoryExternalId) ?? null : null;
    $[70] = lookups?.subcategoryMap;
    $[71] = triggerPin;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  const triggerSubcategory = t27;
  const triggerTitle = triggerPin?.properties.title || triggerSubcategory?.title || "";
  let t28;
  if ($[73] !== analytics || $[74] !== externalMapId || $[75] !== heroAction || $[76] !== hostType || $[77] !== selectMarkerInMap) {
    t28 = id => {
      selectMarkerInMap(id, {
        flyTo: true,
        selectSource: "sidebar_hero_action"
      });
      if (heroAction) {
        analytics.event("game_map_hero_action_result_pin_select", {
          mapId: externalMapId ?? "",
          heroActionId: heroAction.externalId,
          heroActionName: heroAction.name,
          pinId: id,
          platform: hostType
        });
      }
    };
    $[73] = analytics;
    $[74] = externalMapId;
    $[75] = heroAction;
    $[76] = hostType;
    $[77] = selectMarkerInMap;
    $[78] = t28;
  } else {
    t28 = $[78];
  }
  const handleSelectItem = t28;
  let t29;
  if ($[79] !== analytics || $[80] !== externalMapId || $[81] !== heroAction || $[82] !== hostType || $[83] !== onBack) {
    t29 = () => {
      if (heroAction) {
        analytics.event("game_map_hero_action_sidebar_close", {
          mapId: externalMapId ?? "",
          heroActionId: heroAction.externalId,
          heroActionName: heroAction.name,
          platform: hostType
        });
      }
      onBack();
    };
    $[79] = analytics;
    $[80] = externalMapId;
    $[81] = heroAction;
    $[82] = hostType;
    $[83] = onBack;
    $[84] = t29;
  } else {
    t29 = $[84];
  }
  const handleBack = t29;
  if (!heroAction) {
    return null;
  }
  let t30;
  if ($[85] !== t) {
    t30 = t(_temp5);
    $[85] = t;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  let t31;
  if ($[87] !== t) {
    t31 = t(_temp6);
    $[87] = t;
    $[88] = t31;
  } else {
    t31 = $[88];
  }
  let t32;
  if ($[89] !== canShowSave || $[90] !== handleSaveClick || $[91] !== t) {
    t32 = canShowSave ? <Button onClick={handleSaveClick} color="primary" size="md">{t(_temp7)}</Button> : null;
    $[89] = canShowSave;
    $[90] = handleSaveClick;
    $[91] = t;
    $[92] = t32;
  } else {
    t32 = $[92];
  }
  let t33;
  if ($[93] !== heroAction.iconCategory) {
    t33 = getHeroActionIcon(heroAction.iconCategory);
    $[93] = heroAction.iconCategory;
    $[94] = t33;
  } else {
    t33 = $[94];
  }
  let t34;
  if ($[95] !== selectMarkerInMap || $[96] !== triggerPinId) {
    t34 = () => triggerPinId && selectMarkerInMap(triggerPinId, {
      flyTo: true,
      selectSource: "sidebar_hero_action"
    });
    $[95] = selectMarkerInMap;
    $[96] = triggerPinId;
    $[97] = t34;
  } else {
    t34 = $[97];
  }
  const t35 = triggerTitle || heroAction.name;
  const t36 = !triggerPinId;
  let t37;
  if ($[98] !== t33 || $[99] !== t34 || $[100] !== t35 || $[101] !== t36 || $[102] !== triggerTitle) {
    t37 = <ExploreActionHeaderIcon icon={t33} tone="heroAction" fill={true} onClick={t34} title={triggerTitle} aria-label={t35} disabled={t36} />;
    $[98] = t33;
    $[99] = t34;
    $[100] = t35;
    $[101] = t36;
    $[102] = triggerTitle;
    $[103] = t37;
  } else {
    t37 = $[103];
  }
  let t38;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = css({
      textAlign: "center",
      wordBreak: "break-word"
    });
    $[104] = t38;
  } else {
    t38 = $[104];
  }
  let t39;
  if ($[105] !== heroAction.name || $[106] !== t38) {
    t39 = <Type variant="header-m" color="white" className={t38}>{heroAction.name}</Type>;
    $[105] = heroAction.name;
    $[106] = t38;
    $[107] = t39;
  } else {
    t39 = $[107];
  }
  let t40;
  if ($[108] !== heroAction.description || $[109] !== heroAction.externalId || $[110] !== heroAction.name) {
    t40 = <HeroActionDescription description={heroAction.description} heroActionId={heroAction.externalId} heroActionName={heroAction.name} variant="hero" />;
    $[108] = heroAction.description;
    $[109] = heroAction.externalId;
    $[110] = heroAction.name;
    $[111] = t40;
  } else {
    t40 = $[111];
  }
  let t41;
  if ($[112] !== t39 || $[113] !== t40) {
    t41 = <FlexColumn align="center" gap="4" width="full">{t39}{t40}</FlexColumn>;
    $[112] = t39;
    $[113] = t40;
    $[114] = t41;
  } else {
    t41 = $[114];
  }
  let t42;
  if ($[115] !== t37 || $[116] !== t41) {
    t42 = <FlexColumn align="center" gap="12" width="full" data-drawer-drag="true">{t37}{t41}</FlexColumn>;
    $[115] = t37;
    $[116] = t41;
    $[117] = t42;
  } else {
    t42 = $[117];
  }
  const t43 = !isLoggedIn ? openChecklistItemLoginModal : undefined;
  let t44;
  if ($[118] !== foundCount || $[119] !== t43 || $[120] !== totalCount) {
    t44 = <MapDiscoveryProgress foundCount={foundCount} totalCount={totalCount} onClick={t43} />;
    $[118] = foundCount;
    $[119] = t43;
    $[120] = totalCount;
    $[121] = t44;
  } else {
    t44 = $[121];
  }
  let t45;
  if ($[122] !== completionFilter || $[123] !== onSearchFocus || $[124] !== searchQuery) {
    t45 = <SearchFilters searchQuery={searchQuery} onSearchChange={setSearchQuery} completionFilter={completionFilter} onCompletionFilterChange={setCompletionFilter} inputRef={searchInputRef} onFocus={onSearchFocus} />;
    $[122] = completionFilter;
    $[123] = onSearchFocus;
    $[124] = searchQuery;
    $[125] = t45;
  } else {
    t45 = $[125];
  }
  let t46;
  if ($[126] !== t44 || $[127] !== t45) {
    t46 = <FlexColumn gap="8" width="full">{t44}{t45}</FlexColumn>;
    $[126] = t44;
    $[127] = t45;
    $[128] = t46;
  } else {
    t46 = $[128];
  }
  let t47;
  if ($[129] !== checkedItems || $[130] !== groupedItems || $[131] !== handleSelectItem || $[132] !== handleToggleWithLogin || $[133] !== hasAnyItems || $[134] !== searchQuery || $[135] !== selectedMarkerId || $[136] !== t) {
    t47 = !hasAnyItems ? <FlexColumn width="full" gap="12" align="center"><Type variant="header-s" color="text.secondary">{searchQuery.trim() ? t(_temp8) : t(_temp9)}</Type></FlexColumn> : Object.entries(groupedItems).map(t48 => {
      const [categoryTitle, categoryItems] = t48;
      return <Collapsible key={categoryTitle} variant="ghost" defaultOpen={true} className={{
        width: "full"
      }} iconProps={{
        size: "md"
      }} trigger={<Type variant="caption" color="white">{categoryTitle}</Type>} triggerClassName={{
        borderRadius: "8",
        justifyContent: "space-between",
        gap: "8"
      }} triggerStyle={{
        padding: "4px 4px 4px 8px"
      }} contentClassName={{
        padding: "0",
        paddingTop: "8"
      }}><FlexColumn width="full" gap="8">{categoryItems.map(item_0 => <ChecklistItemRow key={item_0.id} item={item_0} isChecked={checkedItems.has(item_0.id)} isSelected={selectedMarkerId === item_0.id} onToggle={handleToggleWithLogin} onShowOnMap={handleSelectItem} />)}</FlexColumn></Collapsible>;
    });
    $[129] = checkedItems;
    $[130] = groupedItems;
    $[131] = handleSelectItem;
    $[132] = handleToggleWithLogin;
    $[133] = hasAnyItems;
    $[134] = searchQuery;
    $[135] = selectedMarkerId;
    $[136] = t;
    $[137] = t47;
  } else {
    t47 = $[137];
  }
  let t48;
  if ($[138] !== t47) {
    t48 = <FlexColumn gap="10" width="full">{t47}</FlexColumn>;
    $[138] = t47;
    $[139] = t48;
  } else {
    t48 = $[139];
  }
  let t49;
  if ($[140] !== handleFeedbackRate || $[141] !== hasAnyItems) {
    t49 = hasAnyItems && <ChecklistFeedback onRate={handleFeedbackRate} />;
    $[140] = handleFeedbackRate;
    $[141] = hasAnyItems;
    $[142] = t49;
  } else {
    t49 = $[142];
  }
  let t50;
  if ($[143] !== backgroundImageUrl || $[144] !== handleBack || $[145] !== isLoginModalOpen || $[146] !== isMobilePane || $[147] !== loginModalTrigger || $[148] !== onSearchFocus || $[149] !== t30 || $[150] !== t31 || $[151] !== t32 || $[152] !== t42 || $[153] !== t46 || $[154] !== t48 || $[155] !== t49) {
    t50 = <SidebarHeroFrame backgroundImageUrl={backgroundImageUrl} backgroundImageStyleOverride={HERO_IMAGE_SIZE_OVERRIDE} isMobilePane={isMobilePane} onBack={handleBack} backLabel={t30} searchAriaLabel={t31} searchInputRef={searchInputRef} isLoginModalOpen={isLoginModalOpen} onLoginModalOpenChange={handleLoginModalOpenChange} loginModalTrigger={loginModalTrigger} onSearchFocus={onSearchFocus} gap="16" headerAction={t32}>{t42}{t46}{t48}{t49}</SidebarHeroFrame>;
    $[143] = backgroundImageUrl;
    $[144] = handleBack;
    $[145] = isLoginModalOpen;
    $[146] = isMobilePane;
    $[147] = loginModalTrigger;
    $[148] = onSearchFocus;
    $[149] = t30;
    $[150] = t31;
    $[151] = t32;
    $[152] = t42;
    $[153] = t46;
    $[154] = t48;
    $[155] = t49;
    $[156] = t50;
  } else {
    t50 = $[156];
  }
  let t51;
  if ($[157] !== clearSelectedMarkerInMap || $[158] !== dispatch || $[159] !== externalMapId || $[160] !== heroAction.description || $[161] !== heroAction.externalId || $[162] !== heroAction.name || $[163] !== isSaveModalOpen || $[164] !== savePinIds) {
    t51 = externalMapId && <SaveChecklistModal isOpen={isSaveModalOpen} onClose={() => setIsSaveModalOpen(false)} defaultTitle={heroAction.name} defaultDescription={heroAction.description} pinIds={savePinIds} mapExternalId={externalMapId} source="hero_action" heroActionId={heroAction.externalId} heroActionName={heroAction.name} onSaved={saved => {
      clearSelectedMarkerInMap();
      dispatch(setActiveCustomChecklistId(saved.id));
      dispatch(setSidebarView("customChecklist"));
    }} />;
    $[157] = clearSelectedMarkerInMap;
    $[158] = dispatch;
    $[159] = externalMapId;
    $[160] = heroAction.description;
    $[161] = heroAction.externalId;
    $[162] = heroAction.name;
    $[163] = isSaveModalOpen;
    $[164] = savePinIds;
    $[165] = t51;
  } else {
    t51 = $[165];
  }
  let t52;
  if ($[166] !== t50 || $[167] !== t51) {
    t52 = <>{t50}{t51}</>;
    $[166] = t50;
    $[167] = t51;
    $[168] = t52;
  } else {
    t52 = $[168];
  }
  return t52;
};
function _temp(state_0) {
  return state_0.auth.isAuthenticated;
}
function _temp2(f_1) {
  return f_1.properties.externalId;
}
function _temp3(f_2) {
  return f_2.properties.externalId;
}
function _temp4(pin) {
  return getFeatureImageUrl(pin);
}
function _temp5($) {
  return $.session_history.back;
}
function _temp6($_0) {
  return $_0.checklist_pages.search_checklist_items_aria_label;
}
function _temp7($_1) {
  return $_1.save_checklist.save;
}
function _temp8($_2) {
  return $_2.no_results_found;
}
function _temp9($_3) {
  return $_3.hero_action_empty_state;
}